import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const devisSchema = z.object({
  contact_id: z.string().min(1, 'Contact requis'),
  montant_estime: z.number().positive('Montant doit être positif'),
  type_prestation: z.enum(['BTP', 'CEE', 'Mixte']),
  statut: z.enum(['Brouillon', 'Envoyé', 'Relancé', 'Accepté', 'Refusé']),
  description: z.string().optional(),
});

export default function DevisForm({ devis, onSuccess, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [relanceDate, setRelanceDate] = useState(devis?.prochaine_relance_date ? new Date(devis.prochaine_relance_date) : null);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await api.get('/contacts');
      setContacts(response.data);
    } catch (error) {
      console.error('Erreur chargement contacts:', error);
    }
  };

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(devisSchema),
    defaultValues: devis || {
      statut: 'Brouillon',
      type_prestation: 'BTP',
    },
  });

  const contactIdValue = watch('contact_id');
  const typeValue = watch('type_prestation');
  const statutValue = watch('statut');

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const payload = {
        ...data,
        prochaine_relance_date: relanceDate ? relanceDate.toISOString() : null,
      };
      
      if (devis) {
        await api.put(`/devis/${devis.devis_id}`, payload);
      } else {
        await api.post('/devis', payload);
      }
      onSuccess();
    } catch (error) {
      console.error('Erreur sauvegarde devis:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div>
        <Label htmlFor="contact_id">Contact *</Label>
        <Select value={contactIdValue} onValueChange={(value) => setValue('contact_id', value)}>
          <SelectTrigger data-testid="devis-contact-select">
            <SelectValue placeholder="Sélectionner un contact" />
          </SelectTrigger>
          <SelectContent>
            {contacts.map((contact) => (
              <SelectItem key={contact.contact_id} value={contact.contact_id}>
                {contact.nom_prenom} ({contact.contact_id})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.contact_id && <p className="text-sm text-red-600 mt-1">{errors.contact_id.message}</p>}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="type_prestation">Type Prestation *</Label>
          <Select value={typeValue} onValueChange={(value) => setValue('type_prestation', value)}>
            <SelectTrigger data-testid="devis-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="BTP">BTP</SelectItem>
              <SelectItem value="CEE">CEE</SelectItem>
              <SelectItem value="Mixte">Mixte</SelectItem>
            </SelectContent>
          </Select>
          {errors.type_prestation && <p className="text-sm text-red-600 mt-1">{errors.type_prestation.message}</p>}
        </div>

        <div>
          <Label htmlFor="statut">Statut *</Label>
          <Select value={statutValue} onValueChange={(value) => setValue('statut', value)}>
            <SelectTrigger data-testid="devis-statut-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Brouillon">Brouillon</SelectItem>
              <SelectItem value="Envoyé">Envoyé</SelectItem>
              <SelectItem value="Relancé">Relancé</SelectItem>
              <SelectItem value="Accepté">Accepté</SelectItem>
              <SelectItem value="Refusé">Refusé</SelectItem>
            </SelectContent>
          </Select>
          {errors.statut && <p className="text-sm text-red-600 mt-1">{errors.statut.message}</p>}
        </div>
      </div>

      <div>
        <Label htmlFor="montant_estime">Montant Estimé (€) *</Label>
        <Input
          id="montant_estime"
          type="number"
          step="0.01"
          {...register('montant_estime', { valueAsNumber: true })}
          data-testid="devis-montant-input"
        />
        {errors.montant_estime && <p className="text-sm text-red-600 mt-1">{errors.montant_estime.message}</p>}
      </div>

      <div>
        <Label>Prochaine Relance</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-full justify-start text-left font-normal"
              data-testid="devis-relance-date-btn"
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {relanceDate ? format(relanceDate, 'dd/MM/yyyy', { locale: fr }) : 'Sélectionner une date'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar mode="single" selected={relanceDate} onSelect={setRelanceDate} locale={fr} />
          </PopoverContent>
        </Popover>
      </div>

      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea id="description" {...register('description')} rows={4} data-testid="devis-description-input" />
      </div>

      <div className="flex gap-2 justify-end pt-4">
        <Button type="button" variant="outline" onClick={onCancel} data-testid="cancel-btn">
          Annuler
        </Button>
        <Button type="submit" disabled={loading} data-testid="submit-devis-btn">
          {loading ? 'Enregistrement...' : devis ? 'Mettre à jour' : 'Créer'}
        </Button>
      </div>
    </form>
  );
}